// ============================================================================
// governanceDeviations — nominal vs ecarts (MASTER-GEST-GOUVERNANCE-V2 2026-07-26)
// ============================================================================
// Classification pure reutilisable CE / CG / Directeurs. Aucune logique UI.

import type { GovernancePersonRow } from './governanceRows';

export type GovernanceTab = 'ce' | 'cg' | 'directors';

/** Ecarts explicites — jamais communique par la couleur seule (libelle obligatoire). */
export type GovernanceDeviationKind =
  | 'profileMissing'
  | 'ceReadWithoutSeat'
  | 'ceSeatWithoutRead'
  | 'ceGrantRevoked';

export type GovernanceDeviationI18nKey =
  `gest:admin.governance.deviations.${GovernanceDeviationKind}`;

const DEVIATION_I18N: Record<GovernanceDeviationKind, GovernanceDeviationI18nKey> = {
  profileMissing: 'gest:admin.governance.deviations.profileMissing',
  ceReadWithoutSeat: 'gest:admin.governance.deviations.ceReadWithoutSeat',
  ceSeatWithoutRead: 'gest:admin.governance.deviations.ceSeatWithoutRead',
  ceGrantRevoked: 'gest:admin.governance.deviations.ceGrantRevoked',
};

export function deviationI18nKey(kind: GovernanceDeviationKind): GovernanceDeviationI18nKey {
  return DEVIATION_I18N[kind];
}

/** Profil membre absent — commun aux trois onglets. */
export function hasProfileMissing(row: Pick<GovernancePersonRow, 'isEmployed'>): boolean {
  return row.isEmployed === null;
}

/** CE nominal : siege + lecture active (discret en UI). */
export function isCeAccessNominal(row: Pick<GovernancePersonRow, 'ceSeat' | 'ceGrantActive'>): boolean {
  return row.ceSeat && row.ceGrantActive;
}

/** Directeur nominal : profil present et au moins une equipe dirigee. */
export function isDirectorNominal(
  row: Pick<GovernancePersonRow, 'isEmployed' | 'directedTeamNames'>,
): boolean {
  return row.isEmployed !== null && row.directedTeamNames.length >= 1;
}

/** CG nominal : profil present (pas d'ACL CG). */
export function isCgNominal(row: Pick<GovernancePersonRow, 'isEmployed'>): boolean {
  return row.isEmployed !== null;
}

function classifyCeDeviations(row: GovernancePersonRow): GovernanceDeviationKind[] {
  const deviations: GovernanceDeviationKind[] = [];
  if (hasProfileMissing(row)) deviations.push('profileMissing');
  if (row.ceGrantActive && !row.ceSeat) deviations.push('ceReadWithoutSeat');
  if (row.ceSeat && !row.ceGrantActive) deviations.push('ceSeatWithoutRead');
  if (row.grantIsRevoked && !row.ceGrantActive) deviations.push('ceGrantRevoked');
  return deviations;
}

function classifyCgDeviations(row: GovernancePersonRow): GovernanceDeviationKind[] {
  const deviations: GovernanceDeviationKind[] = [];
  if (hasProfileMissing(row)) deviations.push('profileMissing');
  return deviations;
}

function classifyDirectorDeviations(row: GovernancePersonRow): GovernanceDeviationKind[] {
  const deviations: GovernanceDeviationKind[] = [];
  if (hasProfileMissing(row)) deviations.push('profileMissing');
  return deviations;
}

export function classifyGovernanceDeviations(
  row: GovernancePersonRow,
  tab: GovernanceTab,
): GovernanceDeviationKind[] {
  switch (tab) {
    case 'ce':
      return classifyCeDeviations(row);
    case 'cg':
      return classifyCgDeviations(row);
    case 'directors':
      return classifyDirectorDeviations(row);
  }
}

/** Nombre total de situations a verifier (somme des ecarts par ligne). */
export function countGovernanceDeviations(
  rows: readonly GovernancePersonRow[],
  tab: GovernanceTab,
): number {
  return rows.reduce(
    (sum, row) => sum + classifyGovernanceDeviations(row, tab).length,
    0,
  );
}

/** Lignes ayant au moins un ecart (utile pour filtre futur). */
export function filterRowsWithDeviations(
  rows: readonly GovernancePersonRow[],
  tab: GovernanceTab,
): GovernancePersonRow[] {
  return rows.filter((row) => classifyGovernanceDeviations(row, tab).length > 0);
}
